// src/components/CheckoutCsvUploadCard.jsx
//
// CSV upload UI for section rosters. Self-contained component used inside
// the StudentCheckout "Rosters" tab.
//
// CSV format expected:
//   sisId, name [optional], <section_id_1>, <section_id_2>, ...
//
// Where section_id_X columns have header values matching catalog section
// IDs (e.g. "athletics", "science_books"). Cell values are interpreted as
// truthy/falsy:
//   truthy: 'y', 'yes', '1', 'true', 'x', '✓' (case-insensitive, trimmed)
//   falsy:  blank, 'n', 'no', '0', 'false', '-'
//
// The component:
//   1. Lets user paste CSV text or upload a .csv file
//   2. Parses it locally and shows a preview with column / permission analysis
//   3. Per-section, lets user choose Append (default) or Replace mode (if
//      they have rights to that section)
//   4. Submits to uploadSectionRosters Cloud Function
//   5. Shows the per-section result summary

import React, { useMemo, useState, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  CHECKOUT_SECTIONS,
  AUTO_SECTION_IDS,
  SECTION_FIELDS,
  SECTION_FLAG_COLUMNS,
  SECTION_CONTEXT_COLUMNS,
  SECTION_TEACHER_COLUMNS,
  canAdminRoster,
  getSectionConfig,
  KA_COLORS,
} from '../utils/checkoutHelpers';

// ──────────────────────────────────────────────────────────────────────────────

const TRUTHY = new Set(['y', 'yes', '1', 'true', 'x', '✓', 'on']);
const FALSY  = new Set(['', 'n', 'no', '0', 'false', '-', 'off']);

function parseCsvText(text) {
  // Minimal CSV parser. Handles double-quoted fields and embedded commas.
  // Returns { headers: [...], rows: [[...], ...] } or throws on malformed input.
  if (!text || typeof text !== 'string') {
    throw new Error('CSV text is empty.');
  }
  const lines = [];
  let cur = '';
  let inQuotes = false;
  // Walk character by character to handle CRLF and quoted newlines
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (c === '"') {
      // Doubled quote inside a quoted field is an escaped quote
      if (inQuotes && text[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
        cur += c;
      }
    } else if ((c === '\n' || c === '\r') && !inQuotes) {
      if (cur.length > 0 || lines.length > 0) lines.push(cur);
      cur = '';
      // Swallow \r\n
      if (c === '\r' && text[i + 1] === '\n') i += 1;
    } else {
      cur += c;
    }
  }
  if (cur.length > 0) lines.push(cur);

  // Parse each line
  const parseLine = (line) => {
    const fields = [];
    let f = '';
    let inQ = false;
    for (let i = 0; i < line.length; i += 1) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') {
          f += '"';
          i += 1;
        } else {
          inQ = !inQ;
        }
      } else if (c === ',' && !inQ) {
        fields.push(f);
        f = '';
      } else {
        f += c;
      }
    }
    fields.push(f);
    return fields.map((s) => s.trim());
  };

  const allLines = lines.map(parseLine).filter((row) => row.some((v) => v !== ''));
  if (allLines.length === 0) {
    throw new Error('CSV is empty.');
  }
  return { headers: allLines[0], rows: allLines.slice(1) };
}

function interpretCell(value) {
  const v = String(value || '').trim().toLowerCase();
  if (TRUTHY.has(v)) return true;
  if (FALSY.has(v)) return false;
  // Anything else: treat as falsy but flag for warning
  return false;
}

// ──────────────────────────────────────────────────────────────────────────────

const styles = {
  card: {
    background: '#fff',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 6,
    padding: 18,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  title: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: KA_COLORS.navy,
  },
  helpText: {
    fontSize: 12,
    color: '#666',
    margin: '4px 0 14px',
    lineHeight: 1.5,
  },
  fileRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  fileBtn: {
    padding: '6px 12px',
    border: `1px solid ${KA_COLORS.borderGray}`,
    background: '#fff',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    color: KA_COLORS.navy,
  },
  textarea: {
    width: '100%',
    minHeight: 120,
    padding: 8,
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    fontSize: 12,
    fontFamily: 'monospace',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  previewBlock: {
    marginTop: 14,
    padding: 12,
    background: KA_COLORS.lightGray,
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
  },
  previewLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: KA_COLORS.navy,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    margin: '0 0 6px',
  },
  pillRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  sectionPill: (variant) => {
    const palette = {
      writable: { bg: '#D4EDDA', fg: '#155724', border: '#C3E6CB' },
      blocked: { bg: '#FFEBEE', fg: '#C62828', border: '#FFCDD2' },
      auto: { bg: '#E3F2FD', fg: '#1565C0', border: '#BBDEFB' },
      unknown: { bg: '#FFF3CD', fg: '#856404', border: '#FFEEBA' },
    };
    const p = palette[variant] || palette.unknown;
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '3px 8px',
      borderRadius: 12,
      background: p.bg,
      color: p.fg,
      border: `1px solid ${p.border}`,
      fontSize: 11,
      fontWeight: 600,
    };
  },
  replaceRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginTop: 12,
    padding: 10,
    background: '#fff',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
  },
  replaceLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#444',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  submitRow: {
    marginTop: 14,
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  submitBtn: (disabled) => ({
    padding: '8px 16px',
    border: 'none',
    background: disabled ? '#ccc' : KA_COLORS.red,
    color: '#fff',
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 700,
  }),
  cancelBtn: {
    padding: '8px 16px',
    border: `1px solid ${KA_COLORS.borderGray}`,
    background: '#fff',
    color: '#444',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
  },
  errorBox: {
    marginTop: 10,
    padding: 10,
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    borderRadius: 4,
    color: '#C62828',
    fontSize: 13,
  },
  successBox: {
    marginTop: 14,
    padding: 12,
    background: '#fff',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
  },
  resultLine: {
    fontSize: 12,
    color: '#333',
    margin: '3px 0',
  },
};

// ──────────────────────────────────────────────────────────────────────────────

const CheckoutCsvUploadCard = ({ user, defaultSectionId = null, onUploaded }) => {
  const [csvText, setCsvText] = useState('');
  const [parseError, setParseError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [replaceMode, setReplaceMode] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const fileInputRef = useRef(null);

  // Re-parse on text change
  React.useEffect(() => {
    setParseError(null);
    setSubmitResult(null);
    if (!csvText.trim()) {
      setParsed(null);
      return;
    }
    try {
      const { headers, rows } = parseCsvText(csvText);

      // Identify columns by header name. We recognize three kinds:
      //   - section flag columns (truthy/falsy → on/off the roster)
      //   - context columns (free text, attached to a specific section)
      //   - teacher columns (display name + SIS ID, attached to a section)
      //
      // Header matching is case-insensitive and trimmed. The mapping tables
      // (SECTION_FLAG_COLUMNS, SECTION_CONTEXT_COLUMNS, SECTION_TEACHER_COLUMNS)
      // are derived from SECTION_FIELDS in checkoutHelpers.

      const flagColumns = {};        // sectionId → column index
      const contextColumns = {};     // sectionId → column index
      const teacherColumns = {};     // sectionId → { nameIdx?, sisIdIdx? }
      headers.forEach((h, idx) => {
        const key = String(h || '').trim();
        const lkey = key.toLowerCase();
        // Flag column? (header is a section ID, or "hr" alias)
        if (SECTION_FLAG_COLUMNS[lkey]) {
          flagColumns[SECTION_FLAG_COLUMNS[lkey]] = idx;
          return;
        }
        // Context column? (header matches contextField for some section)
        // Match case-sensitively first (since SECTION_CONTEXT_COLUMNS keys
        // are exact field names like 'athletics_sports'), then fall back
        // to lowercase for tolerance.
        const ctxSection =
          SECTION_CONTEXT_COLUMNS[key] || SECTION_CONTEXT_COLUMNS[lkey];
        if (ctxSection) {
          contextColumns[ctxSection] = idx;
          return;
        }
        // Teacher column?
        const tCol = SECTION_TEACHER_COLUMNS[key] || SECTION_TEACHER_COLUMNS[lkey];
        if (tCol) {
          if (!teacherColumns[tCol.sectionId]) teacherColumns[tCol.sectionId] = {};
          if (tCol.kind === 'name') teacherColumns[tCol.sectionId].nameIdx = idx;
          if (tCol.kind === 'sisId') teacherColumns[tCol.sectionId].sisIdIdx = idx;
        }
      });

      const sisIdIdx = headers.findIndex(
        (h) => String(h || '').trim().toLowerCase() === 'sisid',
      );
      const nameIdx = headers.findIndex(
        (h) => String(h || '').trim().toLowerCase() === 'name',
      );

      // Build per-row interpretation
      const interpretedRows = rows.map((cells) => {
        const sisId = sisIdIdx >= 0 ? String(cells[sisIdIdx] || '').trim() : '';
        const name = nameIdx >= 0 ? String(cells[nameIdx] || '').trim() : '';
        const sectionsObj = {};
        Object.entries(flagColumns).forEach(([sid, colIdx]) => {
          sectionsObj[sid] = interpretCell(cells[colIdx]);
        });

        // Per-section detail: context, teacherName, teacherSisId.
        // Only attach to sections that are flagged TRUE (otherwise we'd be
        // carrying detail for sections this student isn't on).
        const detailsObj = {};
        Object.entries(contextColumns).forEach(([sid, colIdx]) => {
          if (sectionsObj[sid] !== true) return;
          const val = String(cells[colIdx] || '').trim();
          if (val) {
            if (!detailsObj[sid]) detailsObj[sid] = {};
            detailsObj[sid].context = val;
          }
        });
        Object.entries(teacherColumns).forEach(([sid, idxs]) => {
          if (sectionsObj[sid] !== true) return;
          const tName =
            idxs.nameIdx != null ? String(cells[idxs.nameIdx] || '').trim() : '';
          const tSisId =
            idxs.sisIdIdx != null ? String(cells[idxs.sisIdIdx] || '').trim() : '';
          if (tName || tSisId) {
            if (!detailsObj[sid]) detailsObj[sid] = {};
            if (tName) detailsObj[sid].teacherName = tName;
            if (tSisId) detailsObj[sid].teacherSisId = tSisId;
          }
        });

        return { sisId, name, sections: sectionsObj, details: detailsObj };
      });

      setParsed({
        headers,
        rows: interpretedRows,
        sectionColumns: Object.keys(flagColumns),
        contextColumns: Object.keys(contextColumns),
        teacherColumns: Object.keys(teacherColumns),
        hasSisIdColumn: sisIdIdx >= 0,
      });
    } catch (err) {
      setParseError(err.message || 'Could not parse CSV.');
      setParsed(null);
    }
  }, [csvText]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCsvText(String(evt.target?.result || ''));
    };
    reader.onerror = () => {
      setParseError('Could not read file.');
    };
    reader.readAsText(file);
  };

  // Per-section permission analysis
  const sectionAnalysis = useMemo(() => {
    if (!parsed) return null;
    return parsed.sectionColumns.map((sid) => {
      const cfg = getSectionConfig(sid);
      if (!cfg) {
        return { sectionId: sid, label: sid, status: 'unknown' };
      }
      if (AUTO_SECTION_IDS.has(sid)) {
        return { sectionId: sid, label: cfg.label, status: 'auto' };
      }
      if (canAdminRoster(user, sid)) {
        return { sectionId: sid, label: cfg.label, status: 'writable' };
      }
      return { sectionId: sid, label: cfg.label, status: 'blocked' };
    });
  }, [parsed, user]);

  const writableSectionIds = useMemo(
    () => (sectionAnalysis || []).filter((s) => s.status === 'writable').map((s) => s.sectionId),
    [sectionAnalysis],
  );

  // How many sections does this user admin overall? Used to decide whether
  // to show the "all sections" template button (a no-op duplicate when the
  // user only admins one section).
  const totalAdminSections = useMemo(
    () =>
      CHECKOUT_SECTIONS
        .map((s) => s.id)
        .filter((sid) => !AUTO_SECTION_IDS.has(sid))
        .filter((sid) => canAdminRoster(user, sid)).length,
    [user],
  );

  const showAllSectionsTemplate =
    totalAdminSections > 1 &&
    defaultSectionId &&
    CHECKOUT_SECTIONS.some((s) => s.id === defaultSectionId);

  const handleSubmit = async () => {
    if (!parsed || writableSectionIds.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitResult(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'uploadSectionRosters');
      const result = await fn({
        rows: parsed.rows,
        replaceModeBySection: replaceMode,
      });
      setSubmitResult(result?.data || null);
      if (onUploaded) onUploaded(result?.data);
    } catch (err) {
      console.error('uploadSectionRosters failed:', err);
      setSubmitError(err.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setCsvText('');
    setParsed(null);
    setParseError(null);
    setReplaceMode({});
    setSubmitError(null);
    setSubmitResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Build "download template" CSV based on what user can admin.
  // scope: 'this-section' (single column for defaultSectionId)
  //        'all-sections' (every section the user can admin)
  const handleDownloadTemplate = (scope = 'this-section') => {
    const adminSectionIds = CHECKOUT_SECTIONS
      .map((s) => s.id)
      .filter((sid) => !AUTO_SECTION_IDS.has(sid))
      .filter((sid) => canAdminRoster(user, sid));
    const focusSection =
      scope === 'this-section' &&
      defaultSectionId &&
      adminSectionIds.includes(defaultSectionId)
        ? defaultSectionId
        : null;
    const sectionCols = focusSection ? [focusSection] : adminSectionIds;
    if (sectionCols.length === 0) {
      alert('No sections you can admin. Ask a superadmin for roster-admin rights.');
      return;
    }

    // Assemble the full ordered list of columns the user gets in the
    // template. For each writable section we emit:
    //   1. The flag column (sectionId, or 'hr' for hr_residency)
    //   2. The contextField if it exists
    //   3. The teacherField + teacherSisIdField if they exist
    const headerCols = ['sisId', 'name'];
    const exampleCols = ['100123', 'Sample Student'];
    const blankCols = ['', ''];

    sectionCols.forEach((sid) => {
      const flagCol = sid === 'hr_residency' ? 'hr' : sid;
      headerCols.push(flagCol);
      exampleCols.push('Y');
      blankCols.push('');

      const def = SECTION_FIELDS[sid] || {};
      if (def.contextField) {
        headerCols.push(def.contextField);
        const hint =
          def.contextField === 'athletics_sports'
            ? 'Soccer, Track'
            : def.contextField === 'library_titles'
              ? 'Hamlet, Macbeth'
              : def.contextField === 'it_notes'
                ? 'MacBook Air'
                : '';
        exampleCols.push(hint);
        blankCols.push('');
      }
      if (def.teacherField) {
        headerCols.push(def.teacherField);
        exampleCols.push('Smith, John');
        blankCols.push('');
      }
      if (def.teacherSisIdField) {
        headerCols.push(def.teacherSisIdField);
        exampleCols.push('107882');
        blankCols.push('');
      }
    });

    const csv = [
      `# Section roster template — generated ${new Date().toLocaleDateString()}`,
      `# Replace the example row below with your actual student SIS IDs.`,
      `# Use Y / Yes / 1 / X for "include in this section". Blank or N / No / 0 to skip.`,
      `# Context columns (athletics_sports, library_titles, it_notes) hold free text:`,
      `#   which sport, which book title, which device. Only filled rows are stored.`,
      `# Teacher columns hold the responsible teacher's display name and SIS ID. The`,
      `#   SIS ID enables "My Pending" to filter to the teacher's own students.`,
      `# The 'name' column is for your reference only — not stored.`,
      `# NOTE: 'finance' has INVERTED polarity — Y means the student STILL OWES`,
      `#   money (pending finance clearance). Blank means cleared (auto-signed).`,
      `#   The upload replaces the finance roster entirely; students NOT listed`,
      `#   with Y will be automatically marked as cleared.`,
      headerCols.join(','),
      exampleCols.join(','),
      blankCols.join(','),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = focusSection
      ? `checkout_template_${focusSection}.csv`
      : 'checkout_template_all_sections.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const validRowCount = parsed
    ? parsed.rows.filter((r) => r.sisId && r.sisId.length > 0).length
    : 0;

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Upload section rosters from CSV</h3>
      <p style={styles.helpText}>
        Paste CSV text below or upload a .csv file. Each row should have a{' '}
        <code>sisId</code> column and one column per section ID (e.g.{' '}
        <code>athletics</code>, <code>science_books</code>). Use Y/Yes/1/X to
        flag a student for inclusion. Sections you don't have admin rights
        for will be skipped silently.
      </p>

      <div style={styles.fileRow}>
        <button
          type="button"
          style={styles.fileBtn}
          onClick={() => fileInputRef.current?.click()}
        >
          Choose CSV file…
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          type="button"
          style={styles.fileBtn}
          onClick={() => handleDownloadTemplate('this-section')}
          title={
            defaultSectionId
              ? `Template with one column: ${defaultSectionId}`
              : 'Template with all sections you can admin'
          }
        >
          {defaultSectionId
            ? 'Download template (this section)'
            : 'Download template'}
        </button>
        {showAllSectionsTemplate && (
          <button
            type="button"
            style={styles.fileBtn}
            onClick={() => handleDownloadTemplate('all-sections')}
            title={`Template with all ${totalAdminSections} sections you can admin`}
          >
            Download template (all sections)
          </button>
        )}
      </div>

      <textarea
        style={styles.textarea}
        placeholder="sisId,name,athletics,science_books&#10;100123,Sample Student,Y,Y&#10;100456,Another Student,N,Y"
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
      />

      {parseError && <div style={styles.errorBox}>{parseError}</div>}

      {parsed && !parseError && (
        <div style={styles.previewBlock}>
          <p style={styles.previewLabel}>Preview</p>
          <p style={{ fontSize: 12, color: '#444', margin: '0 0 8px' }}>
            {validRowCount} row{validRowCount === 1 ? '' : 's'} with valid sisId,{' '}
            {parsed.sectionColumns.length} section column
            {parsed.sectionColumns.length === 1 ? '' : 's'} detected.
          </p>

          {!parsed.hasSisIdColumn && (
            <div style={styles.errorBox}>
              No <code>sisId</code> column found in the header. Add one and try again.
            </div>
          )}

          {parsed.sectionColumns.length === 0 && parsed.hasSisIdColumn && (
            <div style={styles.errorBox}>
              No section columns recognized. Section column headers must match
              section IDs exactly (e.g. <code>athletics</code>,{' '}
              <code>science_books</code>).
            </div>
          )}

          {sectionAnalysis && sectionAnalysis.length > 0 && (
            <div style={styles.pillRow}>
              {sectionAnalysis.map((s) => {
                const labels = {
                  writable: '✓ ' + s.label + ' (you can write)',
                  blocked: '✗ ' + s.label + ' (no permission)',
                  auto: 'ⓘ ' + s.label + ' (auto-derived; skipped)',
                  unknown: '? ' + s.label + ' (unknown column)',
                };
                return (
                  <span key={s.sectionId} style={styles.sectionPill(s.status)}>
                    {labels[s.status]}
                  </span>
                );
              })}
            </div>
          )}

          {writableSectionIds.length > 0 && (
            <div style={styles.replaceRow}>
              <p style={{ ...styles.previewLabel, margin: '0 0 4px' }}>
                Replace mode (use with care)
              </p>
              <p style={{ fontSize: 11, color: '#777', margin: '0 0 8px' }}>
                When unchecked (default): the upload only ADDS the flagged students. Existing
                roster members not in the CSV are kept.<br />
                When checked: the upload becomes the new full roster — students not flagged Y
                in the CSV will be REMOVED.
              </p>
              {writableSectionIds.map((sid) => {
                const cfg = getSectionConfig(sid);
                return (
                  <label key={sid} style={styles.replaceLabel}>
                    <input
                      type="checkbox"
                      checked={!!replaceMode[sid]}
                      onChange={(e) =>
                        setReplaceMode((prev) => ({
                          ...prev,
                          [sid]: e.target.checked,
                        }))
                      }
                    />
                    Replace {cfg?.label || sid}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {submitError && <div style={styles.errorBox}>{submitError}</div>}

      {submitResult && (
        <div style={styles.successBox}>
          <p style={styles.previewLabel}>Upload complete</p>
          {submitResult.invalidSisIds?.length > 0 && (
            <p style={styles.resultLine}>
              <strong style={{ color: '#C62828' }}>
                {submitResult.invalidSisIds.length} unknown SIS ID
                {submitResult.invalidSisIds.length === 1 ? '' : 's'}:
              </strong>{' '}
              {submitResult.invalidSisIds.slice(0, 10).join(', ')}
              {submitResult.invalidSisIds.length > 10 ? '…' : ''}
            </p>
          )}
          {submitResult.skippedSections?.length > 0 && (
            <p style={styles.resultLine}>
              <strong style={{ color: '#856404' }}>Skipped sections:</strong>{' '}
              {submitResult.skippedSections
                .map((s) => `${s.sectionId} (${s.reason})`)
                .join(', ')}
            </p>
          )}
          {Object.entries(submitResult.perSectionResult || {}).map(([sid, r]) => {
            const cfg = getSectionConfig(sid);
            return (
              <p key={sid} style={styles.resultLine}>
                <strong>{cfg?.label || sid}</strong> — added {r.addedCount},
                removed {r.removedCount}, kept {r.unchangedCount}
                {r.recordsUpdatedCount > 0 && (
                  <> &nbsp;•&nbsp; flipped {r.recordsUpdatedCount} pending record(s)</>
                )}
              </p>
            );
          })}
        </div>
      )}

      <div style={styles.submitRow}>
        <button
          type="button"
          style={styles.submitBtn(submitting || !parsed || writableSectionIds.length === 0)}
          onClick={handleSubmit}
          disabled={submitting || !parsed || writableSectionIds.length === 0}
        >
          {submitting ? 'Uploading…' : 'Apply upload'}
        </button>
        <button
          type="button"
          style={styles.cancelBtn}
          onClick={handleReset}
          disabled={submitting}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default CheckoutCsvUploadCard;